import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'

const Schema = z.object({
  name: z.string().min(1, 'Denumirea este obligatorie').max(255),
  cui: z.string().regex(/^(RO)?\d{2,10}$/i, 'CUI invalid'),
  regCom: z.string().min(1, 'Număr Reg. Com. obligatoriu'),
  address: z.string().min(1, 'Adresa obligatorie'),
  iban: z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]+$/i, 'Format IBAN invalid'),
  caen: z.string().regex(/^\d{4}$/, 'CAEN trebuie să aibă 4 cifre'),
  capital: z.coerce.number().nonnegative('Capital nu poate fi negativ'),
})

type Input = z.infer<typeof Schema>

interface ProfileResponse {
  profile: Input | null
}

export function SetariFirmaPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.company:write')

  const query = useQuery<ProfileResponse>({
    queryKey: ['settings', 'company'],
    queryFn: async () => (await api.get<ProfileResponse>('/settings/company')).data,
  })

  const form = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: {
      name: '',
      cui: '',
      regCom: '',
      address: '',
      iban: '',
      caen: '',
      capital: 0,
    },
  })

  useEffect(() => {
    if (query.data?.profile) {
      form.reset(query.data.profile)
    }
  }, [query.data, form])

  const mutation = useMutation({
    mutationFn: async (data: Input) =>
      (await api.put<ProfileResponse>('/settings/company', data)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'company'], data)
      toast.success('Profilul firmei a fost salvat')
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la salvare'
      toast.error(message)
    },
  })

  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-xl font-semibold">Profil firmă</h1>
      <Card description="Datele de identificare ale firmei. Sunt folosite în toate facturile, contractele și rapoartele generate.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : (
          <form
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
            className="space-y-4"
            noValidate
          >
            <Field
              label="Denumire firmă"
              {...form.register('name')}
              error={form.formState.errors.name?.message}
              disabled={!canWrite}
            />
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="CUI"
                hint="Ex: RO12345678 sau 12345678"
                {...form.register('cui')}
                error={form.formState.errors.cui?.message}
                disabled={!canWrite}
              />
              <Field
                label="Nr. Reg. Comerțului"
                hint="Ex: J40/123/2020"
                {...form.register('regCom')}
                error={form.formState.errors.regCom?.message}
                disabled={!canWrite}
              />
            </div>
            <Field
              label="Adresa sediului social"
              {...form.register('address')}
              error={form.formState.errors.address?.message}
              disabled={!canWrite}
            />
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="IBAN principal"
                hint="Format complet ex: RO49AAAA1B31007593840000"
                {...form.register('iban')}
                error={form.formState.errors.iban?.message}
                disabled={!canWrite}
              />
              <Field
                label="Cod CAEN"
                hint="4 cifre"
                {...form.register('caen')}
                error={form.formState.errors.caen?.message}
                disabled={!canWrite}
              />
            </div>
            <Field
              label="Capital social (RON)"
              type="number"
              step="0.01"
              {...form.register('capital')}
              error={form.formState.errors.capital?.message}
              disabled={!canWrite}
            />
            {canWrite && (
              <div className="flex justify-end pt-2">
                <Button type="submit" loading={mutation.isPending}>
                  Salvează
                </Button>
              </div>
            )}
          </form>
        )}
      </Card>
    </div>
  )
}
